import React, { useState, useEffect } from 'react'
import { getVendors, getVendorMenu, placeOrder, getMyOrders, FoodVendor, FoodMenuItem, FoodOrderItem, FoodOrder } from '@/api/foodOrders'
import { ShoppingCart, ChevronLeft, Plus, Minus, Trash2, Store, UtensilsCrossed, AlertCircle, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/helpers'

interface CartItem extends FoodMenuItem {
  quantity: number
  customizations?: Record<string, any>
}

export function ChildFoodOrdering() {
  const [view, setView] = useState<'vendors' | 'menu' | 'cart' | 'orders'>('vendors')
  const [vendors, setVendors] = useState<FoodVendor[]>([])
  const [selectedVendor, setSelectedVendor] = useState<FoodVendor | null>(null)
  const [menuItems, setMenuItems] = useState<FoodMenuItem[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [myOrders, setMyOrders] = useState<FoodOrder[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [specialInstructions, setSpecialInstructions] = useState('')

  useEffect(() => {
    loadVendors()
    loadMyOrders()
  }, [])

  const loadVendors = async () => {
    try {
      setLoading(true)
      const data = await getVendors(true)
      setVendors(data || [])
    } catch (error) {
      console.error('Failed to load vendors:', error)
      toast.error('Failed to load food vendors')
    } finally {
      setLoading(false)
    }
  }

  const loadVendorMenu = async (vendor: FoodVendor) => {
    try {
      setLoading(true)
      setSelectedVendor(vendor)
      const data = await getVendorMenu(vendor.id)
      // Handle both direct array and object with items property
      const items = Array.isArray(data) ? data : (data?.items || data?.menu_items || [])
      setMenuItems(items)
      setView('menu')
    } catch (error) {
      console.error('Failed to load menu:', error)
      toast.error('Failed to load menu')
    } finally {
      setLoading(false)
    }
  }

  const loadMyOrders = async () => {
    try {
      const data = await getMyOrders(20, 0)
      setMyOrders(data || [])
    } catch (error) {
      console.error('Failed to load orders:', error)
    }
  }

  const addToCart = (item: FoodMenuItem) => {
    const existingItem = cart.find(i => i.id === item.id)
    if (existingItem) {
      setCart(cart.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i))
    } else {
      setCart([...cart, { ...item, quantity: 1 }])
    }
    toast.success(`Added ${item.item_name} to cart`)
  }

  const updateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity === 0) {
      setCart(cart.filter(i => i.id !== itemId))
    } else {
      setCart(cart.map(i => i.id === itemId ? { ...i, quantity: newQuantity } : i))
    }
  }

  const clearCart = () => {
    setCart([])
    toast.success('Cart cleared')
  }

  const submitOrder = async () => {
    if (!selectedVendor || cart.length === 0) return

    try {
      setLoading(true)
      const orderItems: FoodOrderItem[] = cart.map(item => ({
        item_id: item.id,
        item_name: item.item_name,
        category: item.category,
        quantity: item.quantity,
        price: item.price,
        customizations: item.customizations
      }))

      await placeOrder(selectedVendor.id, orderItems, undefined, specialInstructions || undefined)

      toast.success('Order submitted! Waiting for parent approval.')
      setCart([])
      setSpecialInstructions('')
      setView('orders')
      loadMyOrders()
    } catch (error: any) {
      console.error('Failed to place order:', error)
      toast.error(getErrorMessage(error, 'Failed to place order'))
    } finally {
      setLoading(false)
    }
  }

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0)
  }

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0)
  }

  const itemsArray = Array.isArray(menuItems) ? menuItems : []
  const categories = ['all', ...new Set(itemsArray.map(item => item.category))]
  const filteredMenuItems = selectedCategory === 'all'
    ? itemsArray
    : itemsArray.filter(item => item.category === selectedCategory)

  // Vendors View
  if (view === 'vendors') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Order Food</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setView('orders')}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
            >
              My Orders ({myOrders.length})
            </button>
            {cart.length > 0 && (
              <button
                onClick={() => setView('cart')}
                className="relative px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <ShoppingCart className="w-4 h-4" />
                Cart ({getCartItemCount()})
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading vendors...</p>
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No food vendors available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(vendor => (
              <button
                key={vendor.id}
                onClick={() => loadVendorMenu(vendor)}
                className="bg-white p-6 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all text-left"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{vendor.vendor_name}</h3>
                    <span className="inline-block px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded mt-1">
                      {vendor.vendor_type.replace('_', ' ')}
                    </span>
                  </div>
                  <Store className="w-8 h-8 text-blue-600" />
                </div>

                {vendor.ordering_days && vendor.ordering_days.length > 0 && (
                  <p className="text-sm text-gray-600 mb-2">
                    Open: {vendor.ordering_days.join(', ')}
                  </p>
                )}

                {vendor.notes && (
                  <p className="text-sm text-gray-500 italic">{vendor.notes}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Menu View
  if (view === 'menu') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setView('vendors')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Vendors
          </button>

          {cart.length > 0 && (
            <button
              onClick={() => setView('cart')}
              className="relative px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Cart ({getCartItemCount()}) - ${getTotalPrice().toFixed(2)}
            </button>
          )}
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">{selectedVendor?.vendor_name}</h2>
          <p className="text-gray-600">{selectedVendor?.vendor_type.replace('_', ' ')}</p>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category === 'all' ? 'All Items' : category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : filteredMenuItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <UtensilsCrossed className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No items in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMenuItems.map(item => {
              const inCart = cart.find(i => i.id === item.id)
              return (
                <div key={item.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.item_name}</h3>
                      <p className="text-lg font-bold text-blue-600">${item.price.toFixed(2)}</p>
                    </div>
                  </div>

                  {item.description && (
                    <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                  )}

                  {item.allergens && item.allergens.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        Contains: {item.allergens.join(', ')}
                      </p>
                    </div>
                  )}

                  {inCart ? (
                    <div className="flex items-center justify-between bg-green-50 rounded-lg p-2">
                      <button
                        onClick={() => updateQuantity(item.id, inCart.quantity - 1)}
                        className="p-1 text-green-700 hover:bg-green-100 rounded"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="font-semibold text-green-700">{inCart.quantity} in cart</span>
                      <button
                        onClick={() => updateQuantity(item.id, inCart.quantity + 1)}
                        className="p-1 text-green-700 hover:bg-green-100 rounded"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => addToCart(item)}
                      disabled={!item.is_available}
                      className={`w-full py-2 rounded-lg font-medium flex items-center justify-center gap-2 ${
                        item.is_available
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <Plus className="w-4 h-4" />
                      {item.is_available ? 'Add to Cart' : 'Not Available'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Cart View
  if (view === 'cart') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setView('menu')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Menu
          </button>

          {cart.length > 0 && (
            <button
              onClick={clearCart}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
            >
              <Trash2 className="w-4 h-4" />
              Clear Cart
            </button>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Cart</h2>

          {cart.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Your cart is empty</p>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {cart.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.item_name}</h3>
                      <p className="text-sm text-gray-600">${item.price.toFixed(2)} each</p>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 bg-white rounded-lg p-1">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1 text-gray-700 hover:bg-gray-100 rounded"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-semibold px-2">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1 text-gray-700 hover:bg-gray-100 rounded"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="text-right min-w-[80px]">
                        <p className="font-bold text-gray-900">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>

                      <button
                        onClick={() => updateQuantity(item.id, 0)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Instructions (Optional)
                  </label>
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Any special requests or dietary needs?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-between text-xl font-bold mb-6">
                  <span>Total:</span>
                  <span className="text-blue-600">${getTotalPrice().toFixed(2)}</span>
                </div>

                <button
                  onClick={submitOrder}
                  disabled={loading}
                  className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Submit Order for Approval
                    </>
                  )}
                </button>

                <p className="text-sm text-gray-600 text-center mt-3">
                  Your order will be sent to your parents for approval
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // Orders View
  if (view === 'orders') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setView('vendors')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Vendors
          </button>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">My Orders</h2>

          {myOrders.length === 0 ? (
            <div className="text-center py-12">
              <UtensilsCrossed className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {myOrders.map(order => (
                <div key={order.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{order.vendor_name}</h3>
                      <p className="text-sm text-gray-600">
                        Ordered: {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      order.status === 'approved' ? 'bg-green-100 text-green-800' :
                      order.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {order.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-3">
                    {order.order_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-700">{item.quantity}x {item.item_name}</span>
                        <span className="text-gray-900 font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="font-semibold">Total:</span>
                    <span className="text-lg font-bold text-blue-600">${order.total_amount.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return null
}
